import { type, type Type } from 'arktype'
import { DateTime, type DurationLikeObject } from 'luxon'

import { assert, TechnicalError } from '@orus.eu/error'
import { leftPad } from '@orus.eu/leftpad'
import type { Language } from '@orus.eu/translations'

export const calendarMonthType: Type<{ year: number; oneBasedMonth: number }> = type({
  year: 'number',
  oneBasedMonth: 'number',
})

/**
 * This utility type represents a month in the calendar.
 */
export type CalendarMonth = typeof calendarMonthType.infer

export function isCalendarMonth(value: unknown): value is CalendarMonth {
  return calendarMonthType.allows(value)
}

export function getLastDayOfMonth(month: CalendarMonth): CalendarDate {
  return getCalendarDateFromTimestamp(
    getStartOfCalendarDate({ ...month, oneBasedDay: 1 }, PARIS)
      .plus({ months: 1 })
      .minus({ days: 1 })
      .toMillis(),
    PARIS,
  )
}

export function getMonthName(month: CalendarMonth, format: 'long' | 'short' = 'long'): string {
  return DateTime.fromObject({ month: month.oneBasedMonth, year: month.year })
    .setLocale('fr')
    .toFormat(format === 'long' ? 'MMMM' : 'MMM')
}

export const calendarDateType: Type<{ year: number; oneBasedMonth: number; oneBasedDay: number }> = type({
  year: 'number',
  oneBasedMonth: 'number',
  oneBasedDay: 'number',
})

/**
 * This utility type represents a date in the calendar, as opposed to a timestamp which represents a specific point in time.
 *
 * The type is not defined using the schema, because we need it to work with TSOA
 */
export type CalendarDate = {
  year: number
  oneBasedMonth: number
  oneBasedDay: number
}

export function isCalendarDate(value: unknown): value is CalendarDate {
  return calendarDateType.allows(value)
}

export class InvalidDateInputError extends Error {
  constructor(timestamp: number, zone: string) {
    super(`Invalid input timestamp=${timestamp}, zone=${zone}`)
  }
}

export function getCalendarDateFromTimestamp(timestamp: number, zone: string): CalendarDate {
  const date = getZonedDateTimeFromMillis(timestamp, zone)
  if (!date.isValid) {
    throw new InvalidDateInputError(timestamp, zone)
  }
  return {
    year: date.year,
    oneBasedMonth: date.month,
    oneBasedDay: date.day,
  }
}

export function getCalendarMonthFromTimestamp(timestamp: number, zone: string): CalendarMonth {
  const date = getZonedDateTimeFromMillis(timestamp, zone)
  if (!date.isValid) {
    throw new InvalidDateInputError(timestamp, zone)
  }
  return {
    year: date.year,
    oneBasedMonth: date.month,
  }
}

export class InvalidCalendarDateInputError extends Error {
  constructor(calendarDate: CalendarDate, zone: string, explanation: string) {
    super(
      `Invalid input calendarDate=${calendarDate.year},${calendarDate.oneBasedMonth},${calendarDate.oneBasedDay}, zone=${zone}, explanation=${explanation}`,
    )
  }
}

export const PARIS = 'Europe/Paris'

export function getStartOfCalendarDate(calendarDate: CalendarDate, zone: string = PARIS): DateTime {
  const date = DateTime.local(calendarDate.year, calendarDate.oneBasedMonth, calendarDate.oneBasedDay, { zone })
  if (!date.isValid) {
    throw new InvalidCalendarDateInputError(calendarDate, zone, date.invalidExplanation ?? date.invalidReason)
  }
  return date
}

export function getEndOfCalendarDate(calendarDate: CalendarDate, zone: string = PARIS): DateTime {
  return getStartOfCalendarDate(calendarDate, zone).endOf('day')
}

export function getTimestampFromCalendarDate(calendarDate: CalendarDate, zone: string = PARIS): number {
  return getStartOfCalendarDate(calendarDate, zone).toMillis()
}

export function formatYyyyMmDd({ year, oneBasedMonth, oneBasedDay }: CalendarDate): string {
  const yyyy = leftPad(year, 4)
  const mm = leftPad(oneBasedMonth, 2)
  const dd = leftPad(oneBasedDay, 2)
  return `${yyyy}-${mm}-${dd}`
}

export function formatDdMmYyyy({ year, oneBasedMonth, oneBasedDay }: CalendarDate): string {
  const yyyy = leftPad(year, 4)
  const mm = leftPad(oneBasedMonth, 2)
  const dd = leftPad(oneBasedDay, 2)
  return `${dd}/${mm}/${yyyy}`
}

export function formatDdMmYyyyhhmm({ year, oneBasedMonth, oneBasedDay }: CalendarDate): string {
  const yyyy = leftPad(year, 4)
  const mm = leftPad(oneBasedMonth, 2)
  const dd = leftPad(oneBasedDay, 2)
  return `${dd}/${mm}/${yyyy} - 00:00`
}

export function formatYyyyMm({ year, oneBasedMonth }: CalendarMonth): string {
  const yyyy = leftPad(year, 4)
  const mm = leftPad(oneBasedMonth, 2)
  return `${yyyy}-${mm}`
}

export function formatMmYyyy({ year, oneBasedMonth }: CalendarMonth): string {
  const yyyy = leftPad(year, 4)
  const mm = leftPad(oneBasedMonth, 2)
  return `${mm}/${yyyy}`
}

export class InvalidCalendarDateFormatError extends Error {
  constructor(str: string) {
    super(`Attempted to parse a string that doesn't match required format yyyy-mm-dd: "${str}"`)
  }
}

export function parseYyyyMm(str: string): CalendarMonth {
  if (!/^[0-9]{4}-[0-9]{2}/g.test(str)) {
    throw new InvalidCalendarDateFormatError(str)
  }

  const numbers = str
    .split('-')
    .map((str) => parseInt(str, 10))
    .filter((n) => !Number.isNaN(n) && Number.isSafeInteger(n))

  const [year, oneBasedMonth] = numbers
  assert(year != null, 'year should be defined')
  assert(oneBasedMonth != null, 'oneBasedMonth should be defined')
  return { year, oneBasedMonth }
}

export function parseYyyyMmDd(str: string): CalendarDate {
  const parsingOutput = tryParseYyyyMmDd(str)
  if (!parsingOutput) {
    throw new InvalidCalendarDateFormatError(str)
  }
  return parsingOutput
}

export function tryParseYyyyMmDd(str: string): CalendarDate | null {
  if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}/g.test(str)) {
    return null
  }

  const numbers = str
    .split('-')
    .map((str) => parseInt(str, 10))
    .filter((n) => !Number.isNaN(n) && Number.isSafeInteger(n))

  const [year, oneBasedMonth, oneBasedDay] = numbers
  assert(year != null, 'year should be defined')
  assert(oneBasedMonth != null, 'oneBasedMonth should be defined')
  assert(oneBasedDay != null, 'oneBasedDay should be defined')
  return { year, oneBasedMonth, oneBasedDay }
}

export function calendarDateEqual(date1: CalendarDate, date2: CalendarDate): boolean {
  return (
    date1.oneBasedDay === date2.oneBasedDay && date1.oneBasedMonth === date2.oneBasedMonth && date1.year === date2.year
  )
}

export function calendarMonthEqual(date1: CalendarMonth, date2: CalendarMonth): boolean {
  return date1.oneBasedMonth === date2.oneBasedMonth && date1.year === date2.year
}

/**
 * date1 < date2
 * */
export function calendarDateLt(date1: CalendarDate, date2: CalendarDate): boolean {
  return getStartOfCalendarDate(date1, PARIS) < getStartOfCalendarDate(date2, PARIS)
}

/**
 * date1 <= date2
 * */
export function calendarDateLte(date1: CalendarDate, date2: CalendarDate): boolean {
  return getStartOfCalendarDate(date1, PARIS) <= getStartOfCalendarDate(date2, PARIS)
}

/**
 * date1 > date2
 * */
export function calendarDateGt(date1: CalendarDate, date2: CalendarDate): boolean {
  return getStartOfCalendarDate(date1, PARIS) > getStartOfCalendarDate(date2, PARIS)
}

/**
 * date1 >= date2
 * */
export function calendarDateGte(date1: CalendarDate, date2: CalendarDate): boolean {
  return getStartOfCalendarDate(date1, PARIS) >= getStartOfCalendarDate(date2, PARIS)
}

export function calendarDateToString(
  calendarDate: CalendarDate,
  zone: string,
  format: 'DATE_FULL' | 'DATE_SHORT' | 'DATE_MED',
  language: Language,
): string {
  const dateTime = getStartOfCalendarDate(calendarDate, zone)

  let luxonFormat
  switch (format) {
    case 'DATE_FULL':
      luxonFormat = DateTime.DATE_FULL
      break
    case 'DATE_SHORT':
      luxonFormat = DateTime.DATE_SHORT
      break
    case 'DATE_MED':
      luxonFormat = DateTime.DATE_MED
      break
  }

  return dateTime.setLocale(language).toLocaleString(luxonFormat)
}

export function addDurationToCalendarDate(
  calendarDate: CalendarDate,
  duration: DurationLikeObject,
  zone: string,
): CalendarDate {
  const luxonDate = getStartOfCalendarDate(calendarDate, zone)
  const luxonDatePlusDuration = luxonDate.plus(duration)
  return getCalendarDateFromTimestamp(luxonDatePlusDuration.toMillis(), zone)
}

export function subtractDurationToCalendarDate(
  calendarDate: CalendarDate,
  duration: DurationLikeObject,
  zone: string,
): CalendarDate {
  const luxonDate = getStartOfCalendarDate(calendarDate, zone)
  const luxonDateMinusDuration = luxonDate.minus(duration)
  return getCalendarDateFromTimestamp(luxonDateMinusDuration.toMillis(), zone)
}

export function getMinCalendarDate(date1: CalendarDate, date2: CalendarDate): CalendarDate {
  return calendarDateLt(date1, date2) ? date1 : date2
}

export function getMaxCalendarDate(date1: CalendarDate, date2: CalendarDate): CalendarDate {
  return calendarDateGt(date1, date2) ? date1 : date2
}

export function computeAgeFromCalendarDate(calendarDate: CalendarDate): number {
  return Math.abs(Math.trunc(getStartOfCalendarDate(calendarDate, PARIS).diffNow('years').years))
}

export function computeYearlyDuration(baseDate: CalendarDate, futureDate: CalendarDate): number {
  return getStartOfCalendarDate(futureDate, PARIS).diff(getStartOfCalendarDate(baseDate, PARIS), 'years').years
}

export function computeAgeFromFutureCalendarDate(birthDate: CalendarDate, futureDate: CalendarDate): number {
  return Math.abs(
    Math.trunc(getStartOfCalendarDate(futureDate, PARIS).diff(getStartOfCalendarDate(birthDate, PARIS), 'years').years),
  )
}

export function computeAgeAsOfReferenceDate(birthDate: CalendarDate, referenceDate: CalendarDate): number {
  return computeAgeFromFutureCalendarDate(birthDate, referenceDate)
}

export function computeBirthDateFromAgeAtReferenceDate(
  age: number,
  referenceDate: CalendarDate,
  zone: string = PARIS,
): CalendarDate {
  return subtractDurationToCalendarDate(referenceDate, { years: age }, zone)
}

/**
 * Use this method rather than directly using `DateTime.fromMillis` to ensure that a zone is always used.
 * @param timestamp timestamp in milliseconds
 * @param zone optional zone to defined, if not defined, default zone is PARIS
 * @returns
 */
export function getZonedDateTimeFromMillis(timestamp: number, zone?: string): DateTime {
  return DateTime.fromMillis(timestamp, { zone: zone ?? PARIS })
}

export function formatTimestampDdMmYyyyAHhMm(timestamp: number): string {
  return DateTime.fromMillis(timestamp, { zone: PARIS }).setLocale('fr').toFormat('dd/MM/yyyy à HH:mm')
}

export function formatTimestampDdMmYyyy(timestamp: number): string {
  return DateTime.fromMillis(timestamp, { zone: PARIS }).setLocale('fr').toFormat('dd/MM/yyyy')
}

/**
 * Split birth date into day, month and year
 * @param date string | undefined
 * @returns { day: number; month: number; year: number } | undefined
 */
export function splitBirthDate(date: string | undefined): CalendarDate | undefined {
  if (!date) {
    return undefined
  }

  const birthDate = new Date(date)
  const day = birthDate.getDate()
  const month = birthDate.getMonth() + 1
  const year = birthDate.getFullYear()

  return { oneBasedDay: day, oneBasedMonth: month, year }
}

export function getFullMonthDifference(from: CalendarDate, to: CalendarDate): number {
  const monthDiff = (to.year - from.year) * 12 + (to.oneBasedMonth - from.oneBasedMonth)
  if (to.oneBasedDay < from.oneBasedDay) return monthDiff - 1
  return monthDiff
}

export function getNextWorkingDay(originalDate: DateTime, zone: string): CalendarDate {
  // We arbitrarily limit the search to 30 days, to avoid infinite loops
  for (let i = 0; i < 30; i++) {
    const nextDay = originalDate.plus({ days: i })
    if (!nextDay.isWeekend) {
      return getCalendarDateFromTimestamp(nextDay.toMillis(), zone)
    }
  }
  throw new TechnicalError('Could not find the next working day', { context: { originalDate } })
}

export const monthValues = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const

export const monthSchema: Type<(typeof monthValues)[number]> = type('===', ...monthValues)
export type Month = typeof monthSchema.infer
